import React from 'react';
import Dropdown from '../ui/Dropdown';
import { TOOLBAR_DISCLOSURE_TRIGGER } from '../ui/Toolbar';
import { MENU_ITEM_NEUTRAL, MENU_SECTION_HEADING_TEXT } from '../OrderActionsMenu';
import { BTN } from '../../constants/buttons';
import { formatDateDayMonth } from '../../utils/dateFormat';

/**
 * The manager timesheets' `Actions ▾` (owner-approved mock, 2026-10-28): the
 * order/hire pages' menu look (`ui/Dropdown` + `MENU_ITEM_NEUTRAL`), two groups —
 *   Export     · Export timesheets…           (opens `TimesheetExportDialog`)
 *   This week  · Payroll run — w/c 26 Oct (PDF) / (CSV)
 * The payroll run is the dialog's defaults made one click: THIS week, everyone,
 * submitted weeks only, the overview + pay summary.
 *
 * 🔒 No gate here: it is rendered only on the MANAGER screen, and the server
 * refuses the export without `canManageBudget` in the active department.
 *
 * `variant="sheet"` renders the same three actions as plain buttons for the phone
 * context sheet (a menu inside a bottom sheet would be a popover in a popover).
 */
export const PAYROLL_RUN_SECTIONS = Object.freeze(['overview', 'summary']);

export function payrollRunRequest(weekStart, format) {
  return { from: weekStart, to: weekStart, unitIds: null, lineageIds: null, includeProjected: false, sections: [...PAYROLL_RUN_SECTIONS], format };
}

/**
 * 🧑‍🔧 `crew` (owner 2026-11-01, "My timesheet" — HOURS ONLY): the same look, the crew's three —
 *   This week   · Export week — w/c 26 Oct (PDF) / (CSV)   (`onExportWeek(format)`)
 *   More weeks  · Export weeks…                            (`onOpenExport` → the dialog's crew mode)
 * Same server rule: the crew door (`GET /my-export`) answers only the caller's own weeks.
 */
function CrewActions({ wc, busy, onOpenExport, onExportWeek, variant }) {
  if (variant === 'sheet') {
    return (
      <div className="flex flex-col gap-2" data-testid="crew-actions-sheet">
        <button type="button" className={`${BTN.secondary} min-h-[44px]`} disabled={busy} onClick={() => onExportWeek('pdf')}
          data-testid="crew-actions-week-pdf-sheet">Export week — w/c {wc} (PDF)</button>
        <button type="button" className={`${BTN.secondary} min-h-[44px]`} disabled={busy} onClick={() => onExportWeek('csv')}
          data-testid="crew-actions-week-csv-sheet">Export week — w/c {wc} (CSV)</button>
        <button type="button" className={`${BTN.secondary} min-h-[44px]`} disabled={busy} onClick={onOpenExport}
          data-testid="crew-actions-export-sheet">Export weeks…</button>
      </div>
    );
  }
  return (
    <Dropdown align="right" haspopup="menu" panelClassName="w-72 p-1" triggerClassName={TOOLBAR_DISCLOSURE_TRIGGER}
      title="My timesheet actions" ariaLabel="My timesheet actions" triggerTestId="crew-actions-menu" label={busy ? 'Exporting…' : 'Actions'}>
      {({ close }) => (
        <div role="menu" aria-label="My timesheet actions">
          <p className={`px-3 pt-2 pb-1 ${MENU_SECTION_HEADING_TEXT}`}>This week</p>
          <button type="button" role="menuitem" className={MENU_ITEM_NEUTRAL} disabled={busy}
            onClick={() => { close(); onExportWeek('pdf'); }} data-testid="crew-actions-week-pdf" title="This week, your own hours — no pay figures">
            Export week — w/c {wc} (PDF)
          </button>
          <button type="button" role="menuitem" className={MENU_ITEM_NEUTRAL} disabled={busy}
            onClick={() => { close(); onExportWeek('csv'); }} data-testid="crew-actions-week-csv" title="This week, your own hours — no pay figures">
            Export week — w/c {wc} (CSV)
            <span className="block text-xs text-gray-500 dark:text-gray-400">Opens in Excel</span>
          </button>
          <p className={`px-3 pt-2 pb-1 ${MENU_SECTION_HEADING_TEXT}`}>More weeks</p>
          <button type="button" role="menuitem" className={MENU_ITEM_NEUTRAL} disabled={busy}
            onClick={() => { close(); onOpenExport(); }} data-testid="crew-actions-export" title="Pick a range of weeks, PDF or CSV">
            Export weeks…
            <span className="block text-xs text-gray-500 dark:text-gray-400">Pick a range of weeks, PDF or CSV</span>
          </button>
        </div>
      )}
    </Dropdown>
  );
}

/**
 * `closeSheet` (optional): the phone context sheet's `close` — every action closes the sheet
 * FIRST, because the sheet sits ABOVE a modal (the export dialog would open under it).
 */
export default function TimesheetActionsMenu({
  weekStart, busy = false, onOpenExport: openExport, onPayrollRun: payrollRun, onExportWeek: exportWeek, crew = false, variant = 'menu', closeSheet = null,
}) {
  const first = (fn) => (...args) => { if (closeSheet) closeSheet(); return fn(...args); };
  const onOpenExport = first(openExport);
  const onPayrollRun = payrollRun ? first(payrollRun) : null;
  const onExportWeek = exportWeek ? first(exportWeek) : null;
  const wc = formatDateDayMonth(weekStart);
  if (crew) return <CrewActions wc={wc} busy={busy} onOpenExport={onOpenExport} onExportWeek={onExportWeek} variant={variant} />;
  if (variant === 'sheet') {
    return (
      <div className="flex flex-col gap-2" data-testid="timesheets-actions-sheet">
        <button type="button" className={`${BTN.secondary} min-h-[44px]`} disabled={busy} onClick={onOpenExport}
          data-testid="timesheets-actions-export-sheet">Export timesheets…</button>
        <button type="button" className={`${BTN.secondary} min-h-[44px]`} disabled={busy} onClick={() => onPayrollRun('pdf')}
          data-testid="timesheets-actions-payroll-pdf-sheet">Payroll run — w/c {wc} (PDF)</button>
        <button type="button" className={`${BTN.secondary} min-h-[44px]`} disabled={busy} onClick={() => onPayrollRun('csv')}
          data-testid="timesheets-actions-payroll-csv-sheet">Payroll run — w/c {wc} (CSV)</button>
      </div>
    );
  }
  return (
    <Dropdown
      align="right"
      haspopup="menu"
      panelClassName="w-72 p-1"
      triggerClassName={TOOLBAR_DISCLOSURE_TRIGGER}
      title="Timesheet actions"
      ariaLabel="Timesheet actions"
      triggerTestId="timesheets-actions-menu"
      label={busy ? 'Exporting…' : 'Actions'}
    >
      {({ close }) => (
        <div role="menu" aria-label="Timesheet actions">
          <p className={`px-3 pt-2 pb-1 ${MENU_SECTION_HEADING_TEXT}`}>Export</p>
          <button type="button" role="menuitem" className={MENU_ITEM_NEUTRAL} disabled={busy}
            onClick={() => { close(); onOpenExport(); }} data-testid="timesheets-actions-export"
            title="Dates, sub-departments, people — CSV or PDF">
            Export timesheets…
            <span className="block text-xs text-gray-500 dark:text-gray-400">Dates, sub-departments, people, CSV or PDF</span>
          </button>
          <p className={`px-3 pt-2 pb-1 ${MENU_SECTION_HEADING_TEXT}`}>This week</p>
          <button type="button" role="menuitem" className={MENU_ITEM_NEUTRAL} disabled={busy}
            onClick={() => { close(); onPayrollRun('pdf'); }} data-testid="timesheets-actions-payroll-pdf"
            title="This week, everyone, submitted weeks only — overview and pay summary">
            Payroll run — w/c {wc} (PDF)
          </button>
          <button type="button" role="menuitem" className={MENU_ITEM_NEUTRAL} disabled={busy}
            onClick={() => { close(); onPayrollRun('csv'); }} data-testid="timesheets-actions-payroll-csv"
            title="This week, everyone, submitted weeks only — overview and pay summary">
            Payroll run — w/c {wc} (CSV)
          </button>
        </div>
      )}
    </Dropdown>
  );
}
